import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { Document, Manifest } from '../api/authoring'
import type { useSelectedNode } from '../api/useSelectedNode'
import { editorFor } from '../editors'
import { typeTitle, uploadsInto } from '../model/manifest'
import type { LinkPath } from '../model/link'
import { BlobUploader } from './BlobUploader'
import { ReferenceEditor } from './ReferenceEditor'
import { Loading, QueryError } from './QueryState'

/**
 * The selected node's editor, beside the tree it was selected in.
 *
 * **Build mode's panel, shared by the two screens that arrange a
 * document.** A page and a standalone component both hold a tree whose
 * nodes are selectable, and until this existed only one of them could
 * edit what it had selected: a Container in a library could be arranged
 * and its blocks could be highlighted, and nothing on the screen would
 * change. An inline child of a standalone component was placeable and
 * uneditable — the same failure the recursive tree fixed for a page,
 * one screen later.
 *
 * It stays a panel rather than opening inline the way content mode's
 * editor does, and that is deliberate: this register draws link ids, pin
 * chips and the link-or-inline distinction, and the panel is where the
 * chrome saying *which document an edit lands in* belongs. An operator
 * needs that sentence; a site's owner is not shown any of it.
 *
 * What it draws is what a node has, in the order the host offers it: the
 * picture where the type holds a file, the edges the type declares, and
 * the type's own editor. Every one of those is asked of the manifest and
 * none of them names a type.
 */
export function NodePanel({
  tenant,
  manifest,
  selection,
  selected,
  document,
  onSelect,
  send,
  busy,
  editsRoot,
  empty,
}: {
  tenant: string
  manifest: Manifest | undefined
  /** The link path the surrounding screen has selected. */
  selection: LinkPath
  /** What that path resolves to, and where its intents go. */
  selected: ReturnType<typeof useSelectedNode>
  /** The document the tree belongs to, for what an uploader already holds. */
  document: Document | undefined
  onSelect: (path: LinkPath) => void
  send: (id: string, intent: string, payload: Record<string, unknown>, path?: LinkPath) => Promise<unknown>
  busy: boolean
  /**
   * Whether an empty selection means this panel edits the document's own
   * fields.
   *
   * The page editor says yes: a page has no settings face, so its panel
   * is where a Page's own title and metadata are written. The component
   * editor says no, because those are a face of their own there and one
   * form drawn on two faces is two places to look for one control.
   */
  editsRoot: boolean
  /** What the panel says instead, when nothing is selected. */
  empty: ReactNode
}) {
  const { target, node, linked } = selected
  // An empty selection is the document's own node, which this panel
  // draws only where it is the one editing it.
  const editing = selection.length > 0 || editsRoot ? node : undefined
  const Editor = editorFor(editing?.type ?? '', manifest)
  const sendHere = (intent: string, payload: Record<string, unknown>) =>
    target ? send(target.id, intent, payload, target.path) : Promise.resolve()

  return (
    <Paper sx={{ p: 2.5, position: { md: 'sticky' }, top: 24 }}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h2">
              {editing ? typeTitle(manifest, editing.type) : 'Nothing selected'}
            </Typography>
            {selection.length > 0 && (
              <Chip size="small" label={selection.join(' › ')} onDelete={() => onSelect([])} />
            )}
          </Stack>
          {target?.crossedLink ? (
            <Typography variant="caption" color="text.secondary">
              A linked component — edits go to <strong>{target.id}</strong>, its own stream, and are visible everywhere
              it is placed.
            </Typography>
          ) : selection.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              Defined inline — edits are a command against this document, carrying the link path.
            </Typography>
          ) : (
            empty
          )}
        </Stack>

        {target?.crossedLink && linked.isLoading && <Loading label="Reading the linked component…" />}
        {target?.crossedLink && linked.error && <QueryError error={linked.error} title="Could not read it" />}

        {/* The uploader is a capability the manifest advertises for the
            component, not part of a type's editor — the same shape as
            pinning, which is offered by the host rather than by the node
            it acts on. Which is also why it is gated on the command
            addressing a whole component: an upload is scoped to one, and
            an inline node is not one. No type is named to decide any of
            it. */}
        {editing && target && target.path.length === 0 && uploadsInto(manifest, editing.type, editing.fields) && (
          <BlobUploader
            tenant={tenant}
            id={target.id}
            document={target.crossedLink ? linked.data : document}
            disabled={busy}
          />
        )}

        {/* The second edge, drawn beside the fields rather than inside
            the type's editor: the intents are the core's, and which types
            declare an edge is the manifest's answer. It sits above the
            form because a Pattern with no declaration has nothing else
            worth filling in. */}
        {editing && target && (!target.crossedLink || linked.data) && (
          <ReferenceEditor
            tenant={tenant}
            manifest={manifest}
            typeName={editing.type}
            references={editing.references}
            fields={editing.fields}
            commandId={target.id}
            commandPath={target.path}
            send={sendHere}
            busy={busy}
          />
        )}

        {editing && target && (!target.crossedLink || linked.data) && (
          <Editor
            tenant={tenant}
            commandId={target.id}
            commandPath={target.path}
            fields={editing.fields}
            children={editing.children}
            references={editing.references}
            manifest={manifest}
            typeName={editing.type}
            register="build"
            busy={busy}
            send={sendHere}
          />
        )}

        {!node && selection.length > 0 && (
          <Alert severity="warning">
            Nothing at this path any more. It may have been removed, or the path descends through a linked child, which
            is not addressable — a linked child&rsquo;s content lives in its own stream.
          </Alert>
        )}
      </Stack>
    </Paper>
  )
}
